/**
 * The Claro conditional-logic engine.
 *
 * THIS FILE EXISTS TWICE AND THE TWO COPIES ARE BYTE-FOR-BYTE IDENTICAL — including this banner,
 * which is why the banner names both paths rather than pointing "over there":
 *   SOURCE OF TRUTH   huaca/src/products/claro/shared/conditions.ts
 *   VENDORED COPY     huaca_app/packages/claro-engine/src/conditions.ts
 * Edit the source of truth, copy it over verbatim, then regenerate the engine hash in BOTH repos'
 * conditions.golden.json (the drift-guard test prints the one-liner). The golden vectors in that
 * file run against this engine in both repos' CI, so drift fails a build on both sides.
 *
 * Zero imports on purpose — dependency-free and pure, so the copy can be a literal copy.
 *
 * It answers three questions from a form (a template or an instance snapshot) + the client's answers:
 *   - computeVisibility → which sections/fields are currently shown
 *   - computeCompletion → which sections are complete + how many required-visible fields remain
 *   - validateConditionRefs → authoring rule: a condition may only reference EARLIER fields (DAG)
 *
 * v1 authoring writes a single `predicate` leaf (or null); `and`/`or` are already valid nodes so
 * grouping can be added later with no migration or engine change.
 *
 * Two semantics are product decisions rather than accidents, and the golden vectors pin them so they
 * cannot drift silently:
 *   - VACUOUS TRUTH IS ASYMMETRIC. `and` with an empty `rules` array is TRUE (element shown); `or`
 *     with an empty `rules` array is FALSE (element hidden). That falls out of every()/some(), but it
 *     means an authoring UI that emits an empty group flips visibility purely on which group type the
 *     author happened to pick — so it is pinned rather than left to chance.
 *   - AN UNKNOWN FIELD REFERENCE IS INDISTINGUISHABLE FROM AN UNANSWERED FIELD. A predicate naming a
 *     field id that is not in the form resolves to null; it never throws. So `is-empty` on an unknown
 *     id shows the guarded element and every other operator hides it. The engine stays total on
 *     malformed input; validateConditionRefs is the authoring-time gate that rejects such forms.
 */

export type ConditionOperator = 'equals' | 'is-one-of' | 'has-any-value' | 'is-empty';

export type Condition =
  | { op: 'and'; rules: Condition[] }
  | { op: 'or'; rules: Condition[] }
  | { op: 'predicate'; field: string; operator: ConditionOperator; value?: unknown };

/** Minimal structural shapes the engine needs; the full Field/Section satisfy these. */
export interface EngineField {
  id: string;
  type: string;
  required: boolean;
  visibleWhen: Condition | null;
}
export interface EngineSection {
  id: string;
  visibleWhen: Condition | null;
  fields: EngineField[];
}
export interface EngineForm {
  sections: EngineSection[];
}

/** An answer as the engine reads it — a scalar value and/or an uploaded-file marker. */
export interface EngineAnswer {
  value?: string | number | null;
  file?: unknown | null;
}
export type AnswerMap = Record<string, EngineAnswer | undefined>;

/** Emptiness is about ABSENCE, not falsiness: 0 and false are answers a customer gave. */
function isEmptyValue(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Is a field considered "filled"? File fields need an uploaded file; others need a non-empty value. */
function isFieldFilled(field: EngineField, answers: AnswerMap): boolean {
  const a = answers[field.id];
  if (field.type === 'file') return a?.file != null;
  return !isEmptyValue(a?.value);
}

function evalCondition(cond: Condition, effective: Map<string, unknown>): boolean {
  switch (cond.op) {
    case 'and':
      return cond.rules.every((r) => evalCondition(r, effective));
    case 'or':
      return cond.rules.some((r) => evalCondition(r, effective));
    case 'predicate': {
      const v = effective.has(cond.field) ? effective.get(cond.field) : null;
      switch (cond.operator) {
        case 'has-any-value':
          return !isEmptyValue(v);
        case 'is-empty':
          return isEmptyValue(v);
        case 'equals':
          return !isEmptyValue(v) && String(v) === String(cond.value);
        case 'is-one-of':
          return Array.isArray(cond.value) && cond.value.map(String).includes(String(v));
        default:
          return false;
      }
    }
    default:
      return false;
  }
}

/**
 * Single forward pass in document order. A field's condition is evaluated against the EFFECTIVE
 * values of earlier fields — where a field hidden by its own condition contributes `null` (cascading
 * hides collapse). The "earlier-only" authoring rule (validateConditionRefs) guarantees no cycles.
 */
export function computeVisibility(form: EngineForm): (answers: AnswerMap) => {
  visibleFieldIds: Set<string>;
  visibleSectionIds: Set<string>;
};
export function computeVisibility(
  form: EngineForm,
  answers: AnswerMap,
): { visibleFieldIds: Set<string>; visibleSectionIds: Set<string> };
export function computeVisibility(form: EngineForm, answers?: AnswerMap) {
  if (answers === undefined) return (a: AnswerMap) => computeVisibility(form, a);
  const effective = new Map<string, unknown>();
  const visibleFieldIds = new Set<string>();
  const visibleSectionIds = new Set<string>();
  for (const section of form.sections) {
    const sectionVisible = section.visibleWhen === null || evalCondition(section.visibleWhen, effective);
    if (sectionVisible) visibleSectionIds.add(section.id);
    for (const field of section.fields) {
      const fieldVisible =
        sectionVisible && (field.visibleWhen === null || evalCondition(field.visibleWhen, effective));
      if (fieldVisible) visibleFieldIds.add(field.id);
      // Record AFTER deciding visibility so later fields reference this one's effective value.
      const raw = field.type === 'file' ? (answers[field.id]?.file != null ? 'file' : null) : answers[field.id]?.value ?? null;
      effective.set(field.id, fieldVisible ? raw : null);
    }
  }
  return { visibleFieldIds, visibleSectionIds };
}

/** A section is complete when all its required+visible fields are filled; a required field hidden by
 *  a condition never blocks completion. */
export function computeCompletion(
  form: EngineForm,
  answers: AnswerMap,
): { completedSectionIds: string[]; requiredOutstanding: number } {
  const { visibleFieldIds, visibleSectionIds } = computeVisibility(form, answers);
  const completedSectionIds: string[] = [];
  let requiredOutstanding = 0;
  for (const section of form.sections) {
    if (!visibleSectionIds.has(section.id)) continue;
    let sectionComplete = true;
    for (const field of section.fields) {
      if (!visibleFieldIds.has(field.id)) continue;
      if (field.required && !isFieldFilled(field, answers)) {
        sectionComplete = false;
        requiredOutstanding++;
      }
    }
    if (sectionComplete) completedSectionIds.push(section.id);
  }
  return { completedSectionIds, requiredOutstanding };
}

/** Overall instance status derived from answers (Pending is the untouched initial state, set at
 *  creation — not derived here). */
export function isFormComplete(form: EngineForm, answers: AnswerMap): boolean {
  return computeCompletion(form, answers).requiredOutstanding === 0;
}

/**
 * Authoring rule: every `predicate.field` in a condition must reference a field that appears strictly
 * EARLIER than the element the condition guards (earlier section, or earlier field in the same
 * section). Returns human-readable violations; empty array = valid. Keeps the dependency graph a DAG.
 */
export function validateConditionRefs(form: EngineForm): string[] {
  const errors: string[] = [];
  const seenFieldIds = new Set<string>();

  const refsOf = (cond: Condition | null): string[] => {
    if (!cond) return [];
    if (cond.op === 'and' || cond.op === 'or') return cond.rules.flatMap(refsOf);
    return [cond.field];
  };

  for (const section of form.sections) {
    for (const ref of refsOf(section.visibleWhen)) {
      if (!seenFieldIds.has(ref)) {
        errors.push(`Section "${section.id}" condition references field "${ref}" that is not defined earlier.`);
      }
    }
    for (const field of section.fields) {
      for (const ref of refsOf(field.visibleWhen)) {
        if (ref === field.id) {
          errors.push(`Field "${field.id}" condition references itself.`);
        } else if (!seenFieldIds.has(ref)) {
          errors.push(`Field "${field.id}" condition references field "${ref}" that is not defined earlier.`);
        }
      }
      seenFieldIds.add(field.id);
    }
  }
  return errors;
}
